import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Toast } from '@koridor/design-system';
import { api, type ConnectionState } from '@koridor/api-client';
import { ConnectionBanner } from './components/ConnectionBanner';
import { GlobalGameDialogs } from './components/GlobalGameDialogs';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';
import { useSettings } from './contexts/SettingsContext';
import { useAuth } from './contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { ensureNotificationPermission, wireMatchNotifications } from './lib/notifications';
import { ensureOnlineMatchSignals } from './lib/online-match-signals';
import { ensureRematchMatchedSignal } from './lib/enter-rematch';
import { ensureInviteAcceptedSignal } from './lib/enter-invite';
import { registerNativePush, flushPendingPushNavigation } from './lib/native-push';
import { applyPwaUpdate } from './lib/pwa';

export function AppShell() {
  const [conn, setConn] = useState<ConnectionState>(navigator.onLine ? 'online' : 'offline');
  const [pwaUpdate, setPwaUpdate] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const { user, isGuest } = useAuth();
  const isGame = location.pathname === '/game';
  const isNative = Capacitor.isNativePlatform();
  // Guest / unauthenticated sessions don't use WS — no reconnect banner.
  const showConnectionBanner = !isGuest && api.isAuthenticated;

  useEffect(() => {
    api.onConnectionChange = setConn;
    if (user && !isGuest && api.isAuthenticated) api.connectWs();
    return () => {
      api.onConnectionChange = null;
    };
  }, [user, isGuest]);

  useEffect(() => {
    if (!user || isGuest || !settings.notificationsEnabled) {
      void registerNativePush(false);
      return wireMatchNotifications(false);
    }
    void registerNativePush(true);
    flushPendingPushNavigation();
    if (!Capacitor.isNativePlatform()) void ensureNotificationPermission();
    return wireMatchNotifications(true);
  }, [user, isGuest, settings.notificationsEnabled]);

  useEffect(() => ensureOnlineMatchSignals(), []);
  useEffect(() => ensureRematchMatchedSignal(), []);
  useEffect(() => ensureInviteAcceptedSignal(), []);

  useEffect(() => {
    const onPwa = () => setPwaUpdate(true);
    window.addEventListener('koridor-pwa-update', onPwa);
    return () => window.removeEventListener('koridor-pwa-update', onPwa);
  }, []);

  return (
    <div
      className={isNative ? 'native-shell' : undefined}
      style={{
        ...(isNative ? {
          height: '100%',
        } : {
          maxWidth: 'var(--screen-max, 440px)',
          margin: '0 auto',
          minHeight: '100dvh',
          height: '100dvh',
        }),
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: isGame ? 'transparent' : 'var(--bg-app)',
        paddingTop: isGame ? 0 : 'env(safe-area-inset-top)',
      }}
    >
      {showConnectionBanner && <ConnectionBanner state={conn} />}
      {!isNative && <PwaInstallPrompt />}
      {pwaUpdate && (
        <div style={{ padding: '8px var(--gutter) 0', flexShrink: 0 }}>
          <Toast tone="accent" title="Доступна новая версия" onClose={() => setPwaUpdate(false)}>
            <button type="button" onClick={() => { void applyPwaUpdate(); }}
              style={{ marginTop: 8, border: 'none', background: 'var(--accent)', color: '#fff', borderRadius: 'var(--radius-sm)', padding: '8px 14px', fontWeight: 600, cursor: 'pointer' }}>
              Обновить
            </button>
          </Toast>
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Outlet />
      </div>
      <GlobalGameDialogs />
    </div>
  );
}
